// src/components/Map.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { db } from '../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import './Map.css';

// --- Leaflet Icon Setup ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// --- Helper: Calculate Distance (Haversine Formula) ---
function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
  const R = 6371; 
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; 
  return d;
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

// --- Helper component to control map view ---
function MapViewHandler({ viewSettings }) {
  const map = useMap();
  useEffect(() => {
    if (!viewSettings) return;

    if (viewSettings.bounds) {
      map.fitBounds(viewSettings.bounds, { padding: [50, 50] });
    } else if (viewSettings.center) {
      map.setView(viewSettings.center, 13);
    }
  }, [viewSettings, map]);
  return null;
}

// --- Helper component to handle Popup updates ---
const PopupContentUpdater = ({ isDetailView, markerRef }) => {
    useEffect(() => {
        if (markerRef.current) {
            const popup = markerRef.current.getPopup();
            if (popup && popup.isOpen()) {
                popup.update();
            }
        }
    }, [isDetailView, markerRef]);
    return null;
};

// --- Helper function to calculate offset positions ---
const calculateSpiderfyPositions = (center, count, distance) => {
  if (count === 1) return [center];
  const positions = [];
  const angleStep = (2 * Math.PI) / count;
  for (let i = 0; i < count; i++) {
    const angle = i * angleStep;
    positions.push([
      center[0] + distance * Math.cos(angle),
      center[1] + distance * Math.sin(angle),
    ]);
  }
  return positions;
};

const Map = () => {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  
  const [detailPinId, setDetailPinId] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [viewSettings, setViewSettings] = useState(null);

  useEffect(() => {
    const fetchResourcesAndCategories = async () => {
      setLoading(true);
      try {
        const resourcesSnapshot = await getDocs(collection(db, 'resources'));
        const resourcesData = resourcesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setResources(resourcesData);

        const categoriesSnapshot = await getDocs(collection(db, 'categories'));
        const categoriesData = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCategories(categoriesData);
      } catch (error) { console.error("Error fetching data: ", error); }
    };

    // UPDATED: Added options object for better mobile accuracy
    const geoOptions = {
        enableHighAccuracy: true, // Force GPS usage on mobile
        timeout: 20000,           // Wait up to 20s before failing
        maximumAge: 1000          // Don't accept cached locations older than 1s
    };

    const fallbackLocation = [33.4152, -111.8315]; // UPDATED: Fallback to Mesa, AZ

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation([position.coords.latitude, position.coords.longitude]);
          fetchResourcesAndCategories();
        },
        (error) => {
          console.error("Geolocation error:", error);
          setUserLocation(fallbackLocation); 
          fetchResourcesAndCategories();
        },
        geoOptions // Pass the new options here
      );
    } else {
      setUserLocation(fallbackLocation); 
      fetchResourcesAndCategories();
    }
  }, []);

  useEffect(() => {
    if (userLocation && resources.length > 0) {
      let closestPin = null;
      let minDistance = Infinity;

      resources.forEach(pin => {
        if (pin.coordinates?.latitude && pin.coordinates?.longitude) {
          const dist = getDistanceFromLatLonInKm(
            userLocation[0],
            userLocation[1],
            pin.coordinates.latitude,
            pin.coordinates.longitude
          );
          if (dist < minDistance) {
            minDistance = dist;
            closestPin = pin;
          }
        }
      });

      const MAX_DISTANCE_KM = 80.5;

      if (closestPin && minDistance <= MAX_DISTANCE_KM) {
        setViewSettings({
          bounds: [
            userLocation,
            [closestPin.coordinates.latitude, closestPin.coordinates.longitude]
          ]
        });
      } else {
        setViewSettings({ center: userLocation });
      }
      setLoading(false);
    } else if (userLocation) {
      setViewSettings({ center: userLocation });
      setLoading(false);
    }
  }, [userLocation, resources]);

  const handleCategoryClick = (categoryId) => setSelectedCategory(categoryId);

  // --- Data Processing ---
  const servicePins = useMemo(() => {
    const flattened = [];
    resources.forEach(resource => {
      if (resource.categoryIDs && resource.categoryIDs.length > 0) {
        resource.categoryIDs.forEach(catId => {
          flattened.push({
            ...resource,
            categoryID: catId, 
            uniqueId: `${resource.id}-${catId}`,
          });
        });
      }
    });
    return flattened;
  }, [resources]);

  const filteredPins = useMemo(() => {
    return selectedCategory
      ? servicePins.filter(pin => pin.categoryID === selectedCategory)
      : servicePins;
  }, [servicePins, selectedCategory]);

  const spiderfiedPins = useMemo(() => {
    const coordinateMap = new window.Map();
    filteredPins.forEach(pin => {
      if (pin.coordinates?.latitude && pin.coordinates?.longitude) {
        const key = `${pin.coordinates.latitude},${pin.coordinates.longitude}`;
        if (!coordinateMap.has(key)) {
          coordinateMap.set(key, []);
        }
        coordinateMap.get(key).push(pin);
      }
    });

    const processedPins = [];
    const SPIDERFY_DISTANCE = 0.00015;

    coordinateMap.forEach((group) => {
      const center = [group[0].coordinates.latitude, group[0].coordinates.longitude];
      const positions = calculateSpiderfyPositions(center, group.length, SPIDERFY_DISTANCE);
      group.forEach((pin, index) => {
        processedPins.push({ ...pin, displayPosition: positions[index] });
      });
    });
    
    return processedPins;
  }, [filteredPins]);

  // --- Helper to render formatted text ---
  const renderFormattedMessage = (text) => {
      if (!text) return null;
      const formattedText = text.replace(/\n/g, '<br />');
      return (
          <p 
            className="popup-message-text" 
            dangerouslySetInnerHTML={{ __html: formattedText }} 
          />
      );
  };

  if (loading || !viewSettings) return <div>Loading map...</div>;

  return (
    <div className="map-page-container">
      <div className="filter-panel">
        <h3 className="filter-title">Filter by Category:</h3>
        <div className="filter-buttons-container">
          <button
            onClick={() => handleCategoryClick('')}
            className={selectedCategory === '' ? 'filter-button active' : 'filter-button'}
          >
            All
          </button>
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category.id)}
              className={selectedCategory === category.id ? 'filter-button active' : 'filter-button'}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      <MapContainer center={viewSettings.center || userLocation} zoom={13} className="map-view" zoomControl={false}>
        <TileLayer 
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' 
        />
        <ZoomControl position="topright" />
        <MapViewHandler viewSettings={viewSettings} />

        {spiderfiedPins.map(pin => {
          const position = pin.displayPosition;
          if (!position) return null;

          const categoryForIcon = categories.find(cat => cat.id === pin.categoryID);
          if (!categoryForIcon?.iconUrl) return null;

          const customIcon = new L.Icon({
            iconUrl: categoryForIcon.iconUrl,
            iconAnchor: [18, 35],
            popupAnchor: [0, -35],
            className: 'custom-leaflet-icon'
          });

          const allServicesForPopup = categories.filter(cat => pin.categoryIDs?.includes(cat.id));
          const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLocation[0]},${userLocation[1]}&destination=${pin.coordinates.latitude},${pin.coordinates.longitude}`;
          
          const isDetailView = detailPinId === pin.uniqueId;
          const hasMessage = pin.message && pin.message.trim().length > 0;

          const markerRef = React.createRef();

          return (
            <Marker 
                key={pin.uniqueId} 
                position={position} 
                icon={customIcon}
                ref={markerRef}
                eventHandlers={{
                    click: (e) => {
                        const map = e.target._map;
                        map.flyTo(e.latlng, map.getZoom()); 
                    },
                    popupclose: () => {
                        setDetailPinId(null);
                    }
                }}
            >
              <Popup minWidth={250} maxWidth={400}>
                <PopupContentUpdater isDetailView={isDetailView} markerRef={markerRef} />
                
                <div className={`popup-content ${isDetailView ? 'large-view' : ''}`}>
                  
                  <div className="popup-header">
                    <strong>{pin.organizationName || pin.representativeName}</strong>
                    <p>{pin.location}</p>
                  </div>

                  <div className="popup-services">
                    <p className="services-title">Services Offered:</p>
                    {allServicesForPopup.map(cat => (
                      <div key={cat.id} className="service-item">
                        <img src={cat.iconUrl} alt={cat.name} className="service-icon" />
                        <span>{cat.flagMessage}</span>
                      </div>
                    ))}
                  </div>

                  {!isDetailView ? (
                      <>
                        {hasMessage && (
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation(); 
                                    setDetailPinId(pin.uniqueId);
                                }} 
                                className="directions-button"
                            >
                                More Details
                            </button>
                        )}
                      </>
                  ) : (
                      <div className="popup-message-box">
                          {renderFormattedMessage(pin.message)}
                      </div>
                  )}

                  <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="directions-button">
                    Get Directions
                  </a>

                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default Map;